import React from 'react';

// Components
import { CurrencyCell, DateCell, GrowthCell, PercentageCell, StatCell, TextCell } from '@/views/components/cells';
import ShelfStatus from '@/views/components/shelf-management/ShelfStatus';

// Enums
import { ComplianceComparisonFundLabelKeys, ComplianceComparisonStockLabelKeys } from '../enums/labels';

// Types
import Fund from '@/types/fund';
import Nullable from '@/types/nullable';
import Stock from '@/types/stock';
import User from '@/types/user';

// Utils
import { formatMessage, formatLocalizedString } from '@/utils/i18n';
import { getShelfNameFromShelvableInstrument } from '@/utils/user-shelf';
import { getThemeVersion } from '@/utils/theme';
import { HOLDING_TYPES } from '@/utils/portfolios';
import classNames from 'classnames';
import notification from '@/utils/notification';

export function getInstrumentComparisonConfigs(user?: Nullable<User>) {
  const fundi18nBasePath = 'FundDetails.ComparisonTable';
  const stocki18nBasePath = 'StockDetails.ComparisonTable';
  const isLightTheme = getThemeVersion(user).isLightVersion; // Hook needed to display the V2 of shelf status for light theme only

  // Fund configs
  const fundLabelConfigs = [
    { key: ComplianceComparisonFundLabelKeys.OneYear, label: formatMessage(`${fundi18nBasePath}.1Y`) },
    { key: ComplianceComparisonFundLabelKeys.ThreeYear, label: formatMessage(`${fundi18nBasePath}.3Y`) },
    { key: ComplianceComparisonFundLabelKeys.MER, label: formatMessage(`${fundi18nBasePath}.MER`) },
    { key: ComplianceComparisonFundLabelKeys.Beta, label: formatMessage(`${fundi18nBasePath}.Beta`) },
    { key: ComplianceComparisonFundLabelKeys.Classification, label: formatMessage(`${fundi18nBasePath}.Classification`) },
    { key: ComplianceComparisonFundLabelKeys.RiskRating, label: formatMessage(`${fundi18nBasePath}.RiskRating`) },
    { key: ComplianceComparisonFundLabelKeys.InceptionDate, label: formatMessage(`${fundi18nBasePath}.InceptionDate`) },
    { key: ComplianceComparisonFundLabelKeys.TNA, label: formatMessage(`${fundi18nBasePath}.TNA`) },
  ];

  const fundFieldConfigs = [
    {
      key: ComplianceComparisonFundLabelKeys.Symbol,
      render: (fund: Fund) => (
        <>
          <div className="item-symbol">{fund.symbols?.[0]}</div>
          <div className={classNames('item-name', { 'with-v2': isLightTheme })}>
            <span>{formatLocalizedString(fund.names?.full || { 'en-CA': '', 'fr-CA': '' })}</span>
            {fund.flags && <ShelfStatus statuses={fund.flags} isV2={isLightTheme} title={user ? getShelfNameFromShelvableInstrument(fund, user) : undefined} />}
          </div>
        </>
      ),
    },
    { key: ComplianceComparisonFundLabelKeys.OneYear, render: (fund: Fund) => <GrowthCell value={fund.performance?.percentGrowth?.['1Y'] ?? undefined} /> },
    { key: ComplianceComparisonFundLabelKeys.ThreeYear, render: (fund: Fund) => <GrowthCell value={fund.performance?.percentGrowth?.['3Y'] ?? undefined} /> },
    { key: ComplianceComparisonFundLabelKeys.MER, render: (fund: Fund) => <PercentageCell value={fund.mer ?? undefined} /> },
    { key: ComplianceComparisonFundLabelKeys.Beta, render: (fund: Fund) => <StatCell value={fund.performance?.beta?.['3Y'] ?? undefined} /> },
    { key: ComplianceComparisonFundLabelKeys.Classification, render: (fund: Fund) => formatLocalizedString(fund.classification) },
    { key: ComplianceComparisonFundLabelKeys.RiskRating, render: (fund: Fund) => formatLocalizedString(fund.riskRating) },
    {
      key: ComplianceComparisonFundLabelKeys.InceptionDate,
      render: (fund: Fund) => <DateCell value={fund.dates?.inception ? String(fund.dates.inception) : undefined} />,
    },
    { key: ComplianceComparisonFundLabelKeys.TNA, render: (fund: Fund) => <CurrencyCell value={fund.tna ?? undefined} />, className: 'last-metric' },
  ];

  // Stock configs
  const stockLabelConfigs = [
    { key: ComplianceComparisonStockLabelKeys.OneYear, label: formatMessage(`${stocki18nBasePath}.1Y`) },
    { key: ComplianceComparisonStockLabelKeys.ThreeYear, label: formatMessage(`${stocki18nBasePath}.3Y`) },
    { key: ComplianceComparisonStockLabelKeys.FiveYear, label: formatMessage(`${stocki18nBasePath}.5Y`) },
    { key: ComplianceComparisonStockLabelKeys.Country, label: formatMessage(`${stocki18nBasePath}.Country`) },
    { key: ComplianceComparisonStockLabelKeys.Industry, label: formatMessage(`${stocki18nBasePath}.Industry`) },
    { key: ComplianceComparisonStockLabelKeys.Capitalization, label: formatMessage(`${stocki18nBasePath}.MarketCap`) },
    { key: ComplianceComparisonStockLabelKeys.Beta, label: formatMessage(`${stocki18nBasePath}.Beta`) },
    { key: ComplianceComparisonStockLabelKeys.StdDev, label: formatMessage(`${stocki18nBasePath}.StdDev`) },
    { key: ComplianceComparisonStockLabelKeys.PE, label: formatMessage(`${stocki18nBasePath}.PERatio`) },
    { key: ComplianceComparisonStockLabelKeys.PB, label: formatMessage(`${stocki18nBasePath}.PBRatio`) },
    { key: ComplianceComparisonStockLabelKeys.DistributionYield, label: formatMessage(`${stocki18nBasePath}.DistributionYield`) },
  ];

  const stockFieldConfigs = [
    {
      key: ComplianceComparisonStockLabelKeys.Symbol,
      render: (stock: Stock) => (
        <>
          <div className="item-symbol">{stock.ticker}</div>
          <div className={classNames('item-name', { 'with-v2': isLightTheme })}>
            <span>{stock.companyName}</span>
            {stock.flags && (
              <ShelfStatus statuses={stock.flags} isV2={isLightTheme} title={user ? getShelfNameFromShelvableInstrument(stock, user) : undefined} />
            )}
          </div>
        </>
      ),
    },
    { key: ComplianceComparisonStockLabelKeys.OneYear, render: (stock: Stock) => <GrowthCell value={stock.performance?.percentGrowth?.['1Y'] ?? undefined} /> },
    {
      key: ComplianceComparisonStockLabelKeys.ThreeYear,
      render: (stock: Stock) => <GrowthCell value={stock.performance?.percentGrowth?.['3Y'] ?? undefined} />,
    },
    {
      key: ComplianceComparisonStockLabelKeys.FiveYear,
      render: (stock: Stock) => <GrowthCell value={stock.performance?.percentGrowth?.['5Y'] ?? undefined} />,
    },
    { key: ComplianceComparisonStockLabelKeys.Country, render: (stock: Stock) => <TextCell value={stock.domicile} /> },
    { key: ComplianceComparisonStockLabelKeys.Industry, render: (stock: Stock) => <TextCell value={formatLocalizedString(stock.industry)} /> },
    { key: ComplianceComparisonStockLabelKeys.Capitalization, render: (stock: Stock) => <TextCell value={formatLocalizedString(stock.capitalization)} /> },
    { key: ComplianceComparisonStockLabelKeys.Beta, render: (stock: Stock) => <StatCell value={stock.performance.beta['3Y'] ?? undefined} /> },
    { key: ComplianceComparisonStockLabelKeys.StdDev, render: (stock: Stock) => <StatCell value={stock.performance.standardDeviation['3Y'] ?? undefined} /> },
    { key: ComplianceComparisonStockLabelKeys.PE, render: (stock: Stock) => <StatCell value={stock.peRatio} /> },
    { key: ComplianceComparisonStockLabelKeys.PB, render: (stock: Stock) => <StatCell value={stock.pbRatio} /> },
    { key: ComplianceComparisonStockLabelKeys.DistributionYield, render: (stock: Stock) => <PercentageCell value={stock.distributionYield} /> },
  ];

  // Notification
  function notificationSuccess<T>(items: T[], type: HOLDING_TYPES.FUND | HOLDING_TYPES.STOCK) {
    if (!items || !items.length) return;

    const i18nLabel = type === HOLDING_TYPES.FUND ? `${fundi18nBasePath}.SaveSuccess` : `${stocki18nBasePath}.SaveSuccess`;
    const messageValue =
      type === HOLDING_TYPES.FUND
        ? (items as Fund[]).map((item: Fund) => item.symbols?.[0] || '').join(', ')
        : (items as Stock[]).map((item: Stock) => item.ticker || '').join(', ');

    notification.success(
      formatMessage(i18nLabel, {
        instruments: messageValue,
      }),
    );
  }

  const getConfigs = (type: HOLDING_TYPES.FUND | HOLDING_TYPES.STOCK) =>
    type === HOLDING_TYPES.FUND
      ? { labelConfigs: fundLabelConfigs, fieldConfigs: fundFieldConfigs, selectLabel: formatMessage(`${fundi18nBasePath}.SelectFund`) }
      : { labelConfigs: stockLabelConfigs, fieldConfigs: stockFieldConfigs, selectLabel: formatMessage(`${stocki18nBasePath}.SelectStock`) };

  return {
    getConfigs,
    notificationSuccess,
  };
}
